import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatCuit,
  INVOICE_MODE_LABELS,
  INVOICE_MODES,
  ISSUER_CONDITION_LABELS,
  ISSUER_CONDITIONS,
  VAT_RATE_LABELS,
  VAT_RATES,
  type FiscalConfigDTO,
  type FiscalSettingsInput,
  type FiscalTestResult,
  type VatRate,
} from '@bifeai/shared';
import { Badge, Button, Callout, Card, CardBody, CardHeader, Field, Input, Select, Skeleton, Textarea, useToast } from '@bifeai/ui';
import { Download, FileKey2, PlugZap, Receipt, Save, Store, Upload } from 'lucide-react';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import { useSession } from '../../lib/session';
import { FranchiseStatusCard } from './FranchiseStatusCard';
import { VatDialog } from '../../components/VatDialog';

/** Datos fiscales que se están viendo: los de la carnicería (null) o los de una franquicia. */
const ScopeCtx = createContext<{ scope: string | null; q: string; key: unknown[] }>({ scope: null, q: '', key: ['fiscal', 'config', null] });
const useScope = () => useContext(ScopeCtx);

/**
 * Facturación electrónica ARCA (incluida en todas las licencias). Asistente paso a paso: cada carnicería factura con
 * SU CUIT y SU certificado. BifeAI genera la clave y el pedido; el trámite en ARCA lo hace el dueño.
 */
export function FiscalTab() {
  const { me } = useSession();
  const enabled = me.tenant.modules.includes('ARCA');
  // null = lo que corresponde a quien mira (la carnicería, o su franquicia); si no, la franquicia elegida.
  const [scope, setScope] = useState<string | null>(null);
  const q = scope ? `?branchId=${scope}` : '';
  const key = ['fiscal', 'config', scope];
  const cfg = useQuery({ queryKey: key, queryFn: () => api.get<FiscalConfigDTO>(`/fiscal/config${q}`), enabled });

  if (!enabled) {
    return (
      <Callout tone="info">
        La <strong>facturación electrónica ARCA</strong> es un adicional: con él cada venta puede salir como factura A, B o C con su CAE y código QR,
        y las anulaciones generan la nota de crédito sola. Pedile a BifeAI que lo active.
      </Callout>
    );
  }
  if (!cfg.data) return <Skeleton style={{ height: 320 }} />;
  const c = cfg.data;
  return (
    <ScopeCtx.Provider value={{ scope, q, key }}>
      <div className="bf-form" style={{ gap: 16 }}>
        {c.scopes.length > 1 && (
          <Field
            label="¿Qué razón social estás configurando?"
            hint="Las sucursales propias facturan con el CUIT de la carnicería. Cada franquicia factura con SU CUIT y SU certificado."
          >
            <Select value={scope ?? ''} onChange={(e) => setScope(e.target.value || null)} style={{ maxWidth: 360 }}>
              {c.scopes.map((x) => (
                <option key={x.branchId ?? 'main'} value={x.branchId ?? ''}>
                  {x.branchId ? `Franquicia: ${x.title}` : 'Carnicería (sucursales propias)'}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {c.scope && c.scopes.length === 1 && (
          <Callout tone="info">
            Esta sucursal es una <strong>franquicia</strong>: factura con su propio CUIT y certificado de ARCA.
          </Callout>
        )}
        <FranchiseStatusCard />
        {!c.canManage && (
          <Callout tone="info">La facturación de las sucursales propias la configura la casa central. Acá podés ver cómo está.</Callout>
        )}
        {c.ready ? (
          <Callout tone="success">
            <strong>Listo para facturar</strong>{' '}
            {c.environment === 'HOMO' ? 'en ARCA de prueba (homologación): las facturas no tienen validez fiscal.' : 'en ARCA (producción).'}
          </Callout>
        ) : (
          <Callout tone="warning">
            <strong>Falta para poder facturar:</strong>
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {c.missing.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          </Callout>
        )}
        <SettingsStep cfg={c} />
        <CsrStep cfg={c} />
        <CertificateStep cfg={c} />
        <PointsStep cfg={c} />
        <VatStep cfg={c} />
        <TestStep cfg={c} />
      </div>
    </ScopeCtx.Provider>
  );
}

function Step({
  n,
  title,
  done,
  subtitle,
  children,
  icon,
}: {
  n: number;
  title: string;
  done: boolean;
  subtitle: ReactNode;
  children: ReactNode;
  icon: ReactNode;
}) {
  return (
    <Card>
      <CardHeader
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {icon} {n}. {title} {done && <Badge tone="success">Hecho</Badge>}
          </span>
        }
        subtitle={subtitle}
      />
      <CardBody>{children}</CardBody>
    </Card>
  );
}

function useSaver() {
  const { key: KEY } = useScope();
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function run<T>(fn: () => Promise<T>, ok?: string): Promise<T | null> {
    setBusy(true);
    setError(null);
    try {
      const res = await fn();
      if (res && typeof res === 'object' && 'missing' in res) qc.setQueryData(KEY, res);
      else await qc.invalidateQueries({ queryKey: KEY });
      await qc.invalidateQueries({ queryKey: ['fiscal', 'status'] });
      if (ok) toast.success(ok);
      return res;
    } catch (err) {
      setError(errorMessage(err));
      return null;
    } finally {
      setBusy(false);
    }
  }
  return { busy, error, run };
}

/* ---------------------------------------------------------------- 1. datos fiscales */
function SettingsStep({ cfg }: { cfg: FiscalConfigDTO }) {
  const { q } = useScope();
  const { busy, error, run } = useSaver();
  const [f, setF] = useState<FiscalSettingsInput>(() => toForm(cfg));
  useEffect(() => setF(toForm(cfg)), [cfg]);
  const set = <K extends keyof FiscalSettingsInput>(k: K, v: FiscalSettingsInput[K]) => setF({ ...f, [k]: v });
  const certWillReset = !!cfg.certSubject && (cfg.environment !== f.environment || (cfg.cuit !== null && cfg.cuit !== f.cuit.replace(/\D/g, '')));
  return (
    <Step
      n={1}
      icon={<Receipt size={18} aria-hidden />}
      title="Datos fiscales"
      done={!!cfg.cuit && !!cfg.ivaCondition}
      subtitle={`Los de ${cfg.scope ? 'la franquicia' : 'la carnicería'}, tal cual figuran en ARCA. Salen impresos en cada factura.`}
    >
      <form
        className="bf-form"
        onSubmit={(e) => {
          e.preventDefault();
          void run(() => api.put<FiscalConfigDTO>(`/fiscal/settings${q}`, { ...f }), 'Datos fiscales guardados');
        }}
      >
        <div className="bf-form-row">
          <Field label="CUIT" hint="11 números, con o sin guiones.">
            <Input value={f.cuit} onChange={(e) => set('cuit', e.target.value)} inputMode="numeric" placeholder="20-12345678-9" />
          </Field>
          <Field label="Razón social">
            <Input value={f.legalName} onChange={(e) => set('legalName', e.target.value)} />
          </Field>
          <Field label="Condición frente al IVA" hint={f.ivaCondition === 'RI' ? 'Hace facturas A y B.' : 'Hace facturas C.'}>
            <Select value={f.ivaCondition} onChange={(e) => set('ivaCondition', e.target.value as FiscalSettingsInput['ivaCondition'])}>
              {ISSUER_CONDITIONS.map((k) => (
                <option key={k} value={k}>
                  {ISSUER_CONDITION_LABELS[k]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Ingresos brutos (opcional)">
            <Input value={f.grossIncomeId ?? ''} onChange={(e) => set('grossIncomeId', e.target.value)} />
          </Field>
          <Field label="Inicio de actividades (opcional)">
            <Input type="date" value={f.activityStart ?? ''} onChange={(e) => set('activityStart', e.target.value || null)} />
          </Field>
          <Field label="Domicilio comercial (opcional)">
            <Input value={f.address ?? ''} onChange={(e) => set('address', e.target.value)} />
          </Field>
          <Field label="Ambiente" hint="Empezá en prueba: las facturas de prueba no tienen validez. Cuando todo ande, pasá a producción.">
            <Select value={f.environment} onChange={(e) => set('environment', e.target.value as 'HOMO' | 'PROD')}>
              <option value="HOMO">Prueba (homologación)</option>
              <option value="PROD">Producción (facturas reales)</option>
            </Select>
          </Field>
          <Field label="Al cobrar" hint="“Elegir al cobrar”: el cajero decide en cada venta si sale ticket o factura.">
            <Select value={f.invoiceMode} onChange={(e) => set('invoiceMode', e.target.value as FiscalSettingsInput['invoiceMode'])}>
              {INVOICE_MODES.map((k) => (
                <option key={k} value={k}>
                  {INVOICE_MODE_LABELS[k]}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        {certWillReset && (
          <Callout tone="warning">
            El certificado es de un CUIT y de un ambiente: al guardar este cambio vas a tener que subir el certificado del nuevo ambiente (el pedido
            del paso 2 sirve igual si el CUIT no cambia).
          </Callout>
        )}
        {error && <Callout tone="danger">{error}</Callout>}
        <div>
          <Button type="submit" variant="primary" icon={<Save />} loading={busy}>
            Guardar datos fiscales
          </Button>
        </div>
      </form>
    </Step>
  );
}

function toForm(c: FiscalConfigDTO): FiscalSettingsInput {
  return {
    cuit: c.cuit ? formatCuit(c.cuit) : '',
    legalName: c.legalName ?? '',
    ivaCondition: c.ivaCondition ?? 'MONO',
    grossIncomeId: c.grossIncomeId,
    activityStart: c.activityStart,
    address: c.address,
    environment: c.environment,
    invoiceMode: c.invoiceMode,
  };
}

function download(name: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'application/pkcs10' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* ---------------------------------------------------------------- 2. pedido de certificado */
function CsrStep({ cfg }: { cfg: FiscalConfigDTO }) {
  const { q } = useScope();
  const { busy, error, run } = useSaver();
  const file = `bifeai-${cfg.cuit ?? 'cuit'}.csr`;
  async function generate() {
    if (
      cfg.hasKey &&
      !confirm('Ya hay un pedido generado. Si generás otro, el certificado actual deja de servir y hay que pedir uno nuevo en ARCA. ¿Seguir?')
    )
      return;
    const res = await run(() => api.post<FiscalConfigDTO>(`/fiscal/csr${q}`), 'Pedido generado');
    if (res?.csrPem) download(file, res.csrPem);
  }
  return (
    <Step
      n={2}
      icon={<FileKey2 size={18} aria-hidden />}
      title="Pedido de certificado"
      done={cfg.hasKey}
      subtitle="BifeAI genera la clave secreta de la carnicería (queda guardada cifrada, nunca se muestra) y un archivo .csr para subir a ARCA."
    >
      <div className="bf-form">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button
            variant={cfg.hasKey ? 'secondary' : 'primary'}
            icon={<FileKey2 />}
            loading={busy}
            disabled={!cfg.cuit}
            onClick={() => void generate()}
          >
            {cfg.hasKey ? 'Generar uno nuevo' : 'Generar pedido'}
          </Button>
          {cfg.csrPem && (
            <Button variant="secondary" icon={<Download />} onClick={() => download(file, cfg.csrPem!)}>
              Descargar {file}
            </Button>
          )}
        </div>
        {!cfg.cuit && <p className="bf-hint">Primero guardá los datos fiscales.</p>}
        {error && <Callout tone="danger">{error}</Callout>}
        <ArcaInstructions env={cfg.environment} />
      </div>
    </Step>
  );
}

function ArcaInstructions({ env }: { env: 'HOMO' | 'PROD' }) {
  return env === 'HOMO' ? (
    <ol className="bf-hw-steps">
      <li>
        Entrá a <strong>arca.gob.ar</strong> con la clave fiscal del dueño de la carnicería.
      </li>
      <li>
        Adherí el servicio <strong>“WSASS - Autogestión Certificados Homologación”</strong> (en “Administrador de Relaciones de Clave Fiscal”, si no
        lo tenés).
      </li>
      <li>
        En WSASS: <strong>Nuevo certificado</strong> → poné un nombre (ej. bifeai) → pegá el contenido del archivo .csr → Crear. Copiá el certificado
        que muestra y guardalo como .crt.
      </li>
      <li>
        En WSASS: <strong>Crear autorización a servicio</strong> → elegí el certificado y el servicio <strong>wsfe</strong> (Facturación Electrónica).
      </li>
      <li>Subí el certificado en el paso 3.</li>
    </ol>
  ) : (
    <ol className="bf-hw-steps">
      <li>
        Entrá a <strong>arca.gob.ar</strong> con la clave fiscal (nivel 3) del dueño de la carnicería.
      </li>
      <li>
        Servicio <strong>“Administración de Certificados Digitales”</strong> → Agregar alias (ej. bifeai) → subí el archivo .csr → descargá el
        certificado (.crt).
      </li>
      <li>
        Servicio <strong>“Administrador de Relaciones de Clave Fiscal”</strong> → Nueva relación → ARCA → WebServices →{' '}
        <strong>Facturación Electrónica</strong> → en “Representante” elegí el alias del certificado → Confirmar.
      </li>
      <li>Subí el certificado en el paso 3.</li>
    </ol>
  );
}

/* ---------------------------------------------------------------- 3. certificado */
function CertificateStep({ cfg }: { cfg: FiscalConfigDTO }) {
  const { q } = useScope();
  const { busy, error, run } = useSaver();
  const [pem, setPem] = useState('');
  const expires = cfg.certExpiresAt ? new Date(cfg.certExpiresAt) : null;
  const soon = expires && expires.getTime() - Date.now() < 30 * 86_400_000;
  async function readFile(f: File | undefined) {
    if (f) setPem(await f.text());
  }
  return (
    <Step
      n={3}
      icon={<Upload size={18} aria-hidden />}
      title="Certificado de ARCA"
      done={!!cfg.certSubject}
      subtitle="El archivo .crt que te dio ARCA con el pedido del paso 2."
    >
      <div className="bf-form">
        {cfg.certSubject && (
          <Callout tone={soon ? 'warning' : 'success'}>
            Certificado cargado: <strong>{cfg.certSubject}</strong>
            {expires && <> · vence el {expires.toLocaleDateString('es-AR')}</>}
            {soon && '. Pedí uno nuevo antes de que venza.'}
          </Callout>
        )}
        <Field label="Archivo del certificado">
          <Input type="file" accept=".crt,.pem,.cer,.txt" disabled={!cfg.hasKey} onChange={(e) => void readFile(e.target.files?.[0])} />
        </Field>
        <Field label="…o pegá el contenido" hint="Empieza con -----BEGIN CERTIFICATE-----">
          <Textarea rows={4} value={pem} disabled={!cfg.hasKey} onChange={(e) => setPem(e.target.value)} />
        </Field>
        {error && <Callout tone="danger">{error}</Callout>}
        <div>
          <Button
            variant="primary"
            icon={<Upload />}
            loading={busy}
            disabled={!pem.trim()}
            onClick={() =>
              void run(async () => {
                const r = await api.post<FiscalConfigDTO>(`/fiscal/certificate${q}`, { certPem: pem });
                setPem('');
                return r;
              }, 'Certificado cargado')
            }
          >
            Subir certificado
          </Button>
        </div>
      </div>
    </Step>
  );
}

/* ---------------------------------------------------------------- 4. puntos de venta */
function PointsStep({ cfg }: { cfg: FiscalConfigDTO }) {
  const { q } = useScope();
  const { busy, error, run } = useSaver();
  const [values, setValues] = useState<Record<string, string>>({});
  useEffect(() => setValues(Object.fromEntries(cfg.points.map((p) => [p.branchId, p.ptoVta ? String(p.ptoVta) : '']))), [cfg.points]);
  return (
    <Step
      n={4}
      icon={<Store size={18} aria-hidden />}
      title="Punto de venta de cada sucursal"
      done={cfg.points.some((p) => p.ptoVta)}
      subtitle="Cada sucursal factura con su propio punto de venta de ARCA."
    >
      <div className="bf-form">
        <p className="bf-guide-body" style={{ margin: 0 }}>
          En ARCA, servicio <strong>“Administración de puntos de venta y domicilios”</strong> → Agregar → elegí el sistema{' '}
          <strong>“Factura Electrónica - Web Services”</strong> (para monotributo: “Factura Electrónica - Monotributo - Web Services”). Anotá el
          número y cargalo acá. No uses el punto de venta de “Comprobantes en línea” ni el del controlador fiscal.
        </p>
        <div className="bf-form-row">
          {cfg.points.map((p) => (
            <Field key={p.branchId} label={p.branchName}>
              <Input
                inputMode="numeric"
                placeholder="Ej.: 3"
                value={values[p.branchId] ?? ''}
                onChange={(e) => setValues({ ...values, [p.branchId]: e.target.value.replace(/\D/g, '').slice(0, 5) })}
              />
            </Field>
          ))}
        </div>
        {error && <Callout tone="danger">{error}</Callout>}
        <div>
          <Button
            variant="primary"
            icon={<Save />}
            loading={busy}
            onClick={() =>
              void run(
                () =>
                  api.put<FiscalConfigDTO>(`/fiscal/points${q}`, {
                    points: cfg.points.map((p) => ({ branchId: p.branchId, ptoVta: values[p.branchId] ? Number(values[p.branchId]) : null })),
                  }),
                'Puntos de venta guardados',
              )
            }
          >
            Guardar puntos de venta
          </Button>
        </div>
      </div>
    </Step>
  );
}

/* ---------------------------------------------------------------- 5. IVA */
function VatStep({ cfg }: { cfg: FiscalConfigDTO }) {
  const [vatOpen, setVatOpen] = useState(false);
  const { busy, error, run } = useSaver();
  const [rate, setRate] = useState<VatRate>('21');
  const needed = cfg.ivaCondition === 'RI';
  return (
    <Step
      n={5}
      icon={<Receipt size={18} aria-hidden />}
      title="IVA de los productos"
      done={!needed || cfg.productsWithoutVat === 0}
      subtitle="El precio de venta ya incluye el IVA: la factura lo desarma según la alícuota de cada producto."
    >
      <div className="bf-form">
        {!needed ? (
          <p className="bf-hint" style={{ margin: 0 }}>
            Con monotributo o exento la factura C no discrimina IVA: este paso no hace falta.
          </p>
        ) : (
          <>
            <p className="bf-guide-body" style={{ margin: 0 }}>
              {cfg.productsWithoutVat === 0
                ? 'Todos los productos tienen el IVA cargado.'
                : `Hay ${cfg.productsWithoutVat} producto${cfg.productsWithoutVat === 1 ? '' : 's'} sin IVA cargado.`}{' '}
              Consultá con tu contador la alícuota de cada producto (la carne fresca sin elaborar suele ir al 10,5 %; elaborados, chacinados y
              almacén, al 21 %). Se cambia uno por uno en <Link to="/productos">Productos</Link>, o de a muchos con{' '}
              <strong>Cambiar IVA por categoría o productos</strong>.
            </p>
            <div>
              <Button variant="secondary" onClick={() => setVatOpen(true)}>
                Cambiar IVA por categoría o productos
              </Button>
            </div>
            {vatOpen && <VatDialog onClose={() => setVatOpen(false)} />}
            {cfg.productsWithoutVat > 0 && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <Field label="Alícuota">
                  <Select value={rate} onChange={(e) => setRate(e.target.value as VatRate)}>
                    {VAT_RATES.map((r) => (
                      <option key={r} value={r}>
                        {VAT_RATE_LABELS[r]}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Button
                  variant="secondary"
                  loading={busy}
                  onClick={() => void run(() => api.post<{ updated: number }>('/fiscal/vat', { vatRate: rate, onlyMissing: true }), 'IVA aplicado')}
                >
                  Aplicar a los {cfg.productsWithoutVat} que faltan
                </Button>
              </div>
            )}
          </>
        )}
        {error && <Callout tone="danger">{error}</Callout>}
      </div>
    </Step>
  );
}

/* ---------------------------------------------------------------- 6. prueba */
function TestStep({ cfg }: { cfg: FiscalConfigDTO }) {
  const { q } = useScope();
  const { busy, run } = useSaver();
  const [result, setResult] = useState<FiscalTestResult | null>(null);
  return (
    <Step
      n={6}
      icon={<PlugZap size={18} aria-hidden />}
      title="Probar la conexión con ARCA"
      done={!!cfg.verifiedAt}
      subtitle="Se conecta con ARCA con tu certificado y trae el último número de factura de cada punto de venta. No emite nada."
    >
      <div className="bf-form">
        <div>
          <Button
            variant="primary"
            icon={<PlugZap />}
            loading={busy}
            disabled={!cfg.certSubject}
            onClick={() =>
              void run(async () => {
                const r = await api.post<FiscalTestResult>(`/fiscal/test${q}`);
                setResult(r);
                return r;
              })
            }
          >
            Probar conexión
          </Button>
        </div>
        {result ? (
          <Callout tone={result.ok ? 'success' : 'danger'}>
            {result.message}
            {result.lastNumbers && result.lastNumbers.length > 0 && (
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {result.lastNumbers.map((n) => (
                  <li key={`${n.ptoVta}-${n.letter}`}>
                    Punto de venta {n.ptoVta}, factura {n.letter}: última emitida Nº {n.last}
                  </li>
                ))}
              </ul>
            )}
          </Callout>
        ) : cfg.lastError ? (
          <Callout tone="danger">Última prueba: {cfg.lastError}</Callout>
        ) : cfg.verifiedAt ? (
          <p className="bf-hint" style={{ margin: 0 }}>
            Última prueba correcta: {new Date(cfg.verifiedAt).toLocaleString('es-AR')}
          </p>
        ) : null}
      </div>
    </Step>
  );
}
